/**
 * DismissRecognizer — vertical drag-to-dismiss for modals/sheets.
 *
 * Reports onDismissMove(dy, progress) during drag.
 * Fires onDismiss() when distance OR velocity threshold met.
 * Fires onDismissCancel() when released without meeting threshold.
 */

const DEFAULTS = {
    distanceRatio: 0.3,
    velocity: 0.8,
};

export class DismissRecognizer {
    constructor(config, callbacks) {
        this.config = { ...DEFAULTS, ...config };
        this.callbacks = callbacks;
        this._active = false;
        this._bounds = null;
        this._dismissed = false;
    }

    start(pointerState, bounds) {
        this._active = true;
        this._bounds = bounds;
        this._dismissed = false;
    }

    move(pointerState, axisLock) {
        if (!this._active || !this._bounds) return;

        // Only capture vertical gestures
        if (axisLock && axisLock !== 'vertical') return;

        const dy = pointerState.currentY - pointerState.startY;

        // Only track downward drags
        if (dy <= 0) return;

        const threshold = this._bounds.height * this.config.distanceRatio;
        const progress = Math.min(1, dy / threshold);

        this.callbacks.onDismissMove?.(dy, progress);

        // Check distance threshold during drag
        if (dy >= threshold) {
            this._dismissed = true;
            this._active = false;
            this.callbacks.onDismiss?.();
        }
    }

    end(pointerState, axisLock) {
        if (!this._active || this._dismissed) {
            this._reset();
            return;
        }

        // Only check vertical gestures
        if (axisLock && axisLock !== 'vertical') {
            this.callbacks.onDismissCancel?.();
            this._reset();
            return;
        }

        const dy = pointerState.currentY - pointerState.startY;

        // Check velocity threshold
        const velocity = this._calculateVelocity(pointerState.history);
        if (velocity >= this.config.velocity && dy > 0) {
            this.callbacks.onDismiss?.();
            this._reset();
            return;
        }

        // Distance threshold (final check)
        if (this._bounds && dy >= this._bounds.height * this.config.distanceRatio) {
            this.callbacks.onDismiss?.();
            this._reset();
            return;
        }

        // Neither threshold met — cancel
        this.callbacks.onDismissCancel?.();
        this._reset();
    }

    pause() {
        this._active = false;
    }

    resume(pointerState, bounds) {
        // Don't resume dismiss after pinch
        this._reset();
    }

    cancel() {
        if (this._active && !this._dismissed) {
            this.callbacks.onDismissCancel?.();
        }
        this._reset();
    }

    _calculateVelocity(history) {
        if (history.length < 2) return 0;

        const recent = history.slice(-5);
        const first = recent[0];
        const last = recent[recent.length - 1];
        const dt = last.t - first.t;

        if (dt <= 0) return 0;

        // Vertical velocity only (downward positive)
        return Math.max(0, (last.y - first.y) / dt);
    }

    _reset() {
        this._active = false;
        this._bounds = null;
        this._dismissed = false;
    }

    destroy() {
        this._reset();
    }
}
